import { t } from "../../i18n";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star } from "lucide-react";
import type { Model, ModelPreferences } from "@lumen/shared";
import { useModelCatalog } from "../../ModelCatalogContext";
import { api } from "../../api";

export function FavoriteModelShortcuts() {
  const { models, loadModels } = useModelCatalog();
  const [preferences, setPreferences] = useState<ModelPreferences>();
  const location = useLocation();
  const load = useCallback(
    () =>
      Promise.all([
        loadModels(),
        api<ModelPreferences>("/me/model-preferences"),
      ])
        .then(([, nextPreferences]) => {
          setPreferences(nextPreferences);
        })
        .catch(() => undefined),
    [loadModels],
  );
  useEffect(() => {
    void load();
    const update = (event: Event) =>
      setPreferences((event as CustomEvent<ModelPreferences>).detail);
    window.addEventListener("lumen:model-preferences", update);
    return () => window.removeEventListener("lumen:model-preferences", update);
  }, [load]);
  const favorites = (preferences?.favoriteModelIds ?? [])
    .map((id) => models.find((model) => model.id === id))
    .filter((model): model is Model => Boolean(model));
  if (!favorites.length) return null;
  return (
    <section>
      <div className="flex items-center gap-1.5 px-2 text-[11px] font-bold tracking-[.16em] text-stone-400 uppercase">
        <Star size={11} /> {t("Favorite models")}
      </div>
      <div className="mt-1 space-y-1">
        {favorites.map((model) => (
          <Link
            key={model.id}
            to={`/?model=${encodeURIComponent(model.id)}`}
            aria-label={`Start a new conversation with ${model.name}`}
            title={`New conversation with ${model.name}`}
            className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition hover:bg-white/70 dark:hover:bg-white/5 ${location.pathname === "/" && new URLSearchParams(location.search).get("model") === model.id ? "bg-white font-medium text-lumen-800 shadow-sm dark:bg-white/10 dark:text-lumen-200" : "text-stone-600 dark:text-stone-400"}`}
          >
            <span aria-hidden>{model.icon || "✦"}</span>
            <span className="truncate">{model.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
